import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  authClient,
  clearBearerToken,
  getBearerToken,
} from "../lib/authClient";
import { clearFaroUser, setFaroUser } from "../lib/faro";
import { toNumber } from "../lib/numberFormat";
import { getWalletSummary } from "../api/endpoints/wallet/wallet";
import { getMcpAgentMyToken } from "../api/endpoints/mcp/mcp";
import type { WalletSummary } from "../api/models/walletSummary";

interface SessionUser {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
  emailVerified?: boolean;
  [key: string]: unknown;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const sessionQuery = authClient.useSession();
  const [isConfiguringPasskey, setIsConfiguringPasskey] = useState(false);
  const [passkeyFeedback, setPasskeyFeedback] = useState<
    {
      type: "success" | "error";
      message: string;
    } | null
  >(null);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailFeedback, setEmailFeedback] = useState<
    {
      type: "success" | "error";
      message: string;
    } | null
  >(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [country, setCountry] = useState("");
  const [language, setLanguage] = useState("");
  const [defaultCurrency, setDefaultCurrency] = useState("");
  const [profileFeedback, setProfileFeedback] = useState<
    {
      type: "success" | "error";
      message: string;
    } | null
  >(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [apiResponse, setApiResponse] = useState<unknown | null>(null);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [jwtToken, setJwtToken] = useState<string | null>(null);
  const [jwtLoading, setJwtLoading] = useState(false);
  const [jwtError, setJwtError] = useState<string | null>(null);
  const [copyJwtSuccess, setCopyJwtSuccess] = useState(false);
  const [walletSummary, setWalletSummary] = useState<WalletSummary | null>(
    null,
  );
  const [walletSummaryLoading, setWalletSummaryLoading] = useState(false);
  const [walletSummaryError, setWalletSummaryError] = useState<string | null>(
    null,
  );

  const user: SessionUser | null = useMemo(() => {
    return sessionQuery.data?.user ?? null;
  }, [sessionQuery.data?.user]);

  const userSettings = useMemo(() => {
    return (sessionQuery.data as any)?.userSettings ?? null;
  }, [sessionQuery.data]);

  const apiUrl = import.meta.env.VITE_API_URL || "https://api.wedopay.dev";
  const apiEndpoint = `${apiUrl}/dev/demo/69`;

  // Faz chamada à API de exemplo
  useEffect(() => {
    const fetchApiData = async () => {
      setApiLoading(true);
      setApiError(null);

      try {
        const response = await fetch(apiEndpoint);

        if (!response.ok) {
          throw new Error(`API retornou status ${response.status}`);
        }

        const data = await response.json();
        setApiResponse(data);
      } catch (error) {
        console.error("Erro ao chamar API:", error);
        setApiError(
          error instanceof Error ? error.message : "Erro desconhecido",
        );
      } finally {
        setApiLoading(false);
      }
    };

    fetchApiData();
  }, [apiEndpoint]);

  useEffect(() => {
    let cancelled = false;

    const fetchWalletSummary = async () => {
      setWalletSummaryLoading(true);
      setWalletSummaryError(null);

      try {
        const token = getBearerToken();
        const response = await getWalletSummary(
          token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
        );

        if (cancelled) return;

        if (response.status === 200 && "data" in response.data) {
          setWalletSummary(response.data.data);
        } else {
          setWalletSummaryError(
            `Falha ao buscar resumo da wallet (status ${response.status}).`,
          );
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Erro ao buscar wallet summary:", error);
        setWalletSummaryError(
          error instanceof Error ? error.message : "Erro desconhecido",
        );
      } finally {
        if (!cancelled) setWalletSummaryLoading(false);
      }
    };

    fetchWalletSummary();

    return () => {
      cancelled = true;
    };
  }, []);

  // Busca o token JWT MCP do usuário logado (GET /mcp/my-token)
  useEffect(() => {
    let cancelled = false;

    const fetchJwtToken = async () => {
      if (!user) {
        setJwtToken(null);
        return;
      }

      setJwtLoading(true);
      setJwtError(null);

      try {
        const response = await getMcpAgentMyToken();

        if (cancelled) return;

        if (response.status === 200 && response.data?.token) {
          setJwtToken(response.data.token);
        } else {
          setJwtToken(null);
          setJwtError(
            `Falha ao obter token MCP (status ${response.status}).`,
          );
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Erro ao obter token MCP:", error);
        setJwtToken(null);
        setJwtError(
          error instanceof Error ? error.message : "Erro desconhecido",
        );
      } finally {
        if (!cancelled) setJwtLoading(false);
      }
    };

    fetchJwtToken();

    return () => {
      cancelled = true;
    };
  }, [user]);

  // Vincula o usuário do BetterAuth com o Grafana Faro
  useEffect(() => {
    if (user && sessionQuery.data?.session) {
      setFaroUser({
        id: user.id,
        email: user.email,
        name: user.name,
        sessionId: sessionQuery.data.session.id,
      });
    }
  }, [user, sessionQuery.data?.session]);

  if (sessionQuery.isPending) {
    return (
      <div className="page-centered">
        <span>Carregando perfil...</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="page-centered">
        <div className="auth-card">
          <h1>Ops!</h1>
          <p>Não foi possível carregar os dados do usuário.</p>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/login")}
          >
            Voltar para login
          </button>
        </div>
      </div>
    );
  }

  const handleConfigurePasskey = async () => {
    setPasskeyFeedback(null);

    if (typeof window === "undefined" || !("PublicKeyCredential" in window)) {
      setPasskeyFeedback({
        type: "error",
        message:
          "Seu navegador não suporta passkeys. Tente em outro dispositivo.",
      });
      return;
    }

    setIsConfiguringPasskey(true);
    try {
      const friendlyName = `Dispositivo ${new Date().toLocaleDateString()}`;
      const result = await authClient.passkey.addPasskey({
        name: friendlyName,
        // authenticatorAttachment: "cross-platform",
        // useAutoRegister: true,
      });

      if (result && "error" in result && result.error) {
        setPasskeyFeedback({
          type: "error",
          message: result.error.message ??
            "Não foi possível configurar a passkey. Tente novamente.",
        });
        return;
      }

      setPasskeyFeedback({
        type: "success",
        message:
          "Passkey configurada com sucesso! Agora você pode utilizar o login biométrico.",
      });
    } catch (error) {
      console.error("Erro ao configurar passkey:", error);
      setPasskeyFeedback({
        type: "error",
        message: "Falha inesperada ao configurar passkey. Tente novamente.",
      });
    } finally {
      setIsConfiguringPasskey(false);
    }
  };

  const handleStartEditEmail = () => {
    setNewEmail(user?.email ?? "");
    setIsEditingEmail(true);
    setEmailFeedback(null);
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmail(false);
    setNewEmail("");
    setEmailFeedback(null);
  };

  const handleUpdateEmail = async () => {
    if (!newEmail || !newEmail.trim()) {
      setEmailFeedback({
        type: "error",
        message: "Por favor, insira um email válido.",
      });
      return;
    }

    if (newEmail === user?.email) {
      setEmailFeedback({
        type: "error",
        message: "O novo email é igual ao atual.",
      });
      return;
    }

    setEmailFeedback(null);

    try {
      const response = await authClient.changeEmail({
        newEmail,
        // password: "senha_atual", // Opcional
        callbackURL: `${window.location.origin}/verify-email-change`,
      });

      if (response.error) {
        setEmailFeedback({
          type: "error",
          message: response.error.message ??
            "Erro ao atualizar email. Tente novamente.",
        });
        return;
      }

      setEmailFeedback({
        type: "success",
        message:
          "Email atualizado! Verifique sua caixa de entrada para confirmar o novo email.",
      });
      setIsEditingEmail(false);
      setNewEmail("");

      // Recarrega a sessão para obter os dados atualizados
      sessionQuery.refetch();
    } catch (error) {
      console.error("Erro ao atualizar email:", error);
      setEmailFeedback({
        type: "error",
        message: "Erro ao atualizar email. Tente novamente.",
      });
    }
  };

  const handleStartEditProfile = () => {
    setCountry((user?.country as string) ?? "");
    setLanguage((userSettings?.language as string) ?? "");
    setDefaultCurrency((userSettings?.defaultCurrency as string) ?? "");
    setIsEditingProfile(true);
    setProfileFeedback(null);
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    setCountry("");
    setLanguage("");
    setDefaultCurrency("");
    setProfileFeedback(null);
  };

  const handleUpdateProfile = async () => {
    setProfileFeedback(null);

    try {
      // Usa authClient.updateUser para atualizar campos do usuário
      const response = await authClient.updateUser({
        country: country || null,
        language: language || null,
      } as any);

      if (response.error) {
        setProfileFeedback({
          type: "error",
          message: response.error.message ??
            "Erro ao atualizar perfil. Tente novamente.",
        });
        return;
      }

      if (defaultCurrency) {
        const token = getBearerToken();
        const settingsResponse = await fetch(`${apiUrl}/user/settings`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({ defaultCurrency }),
        });

        if (!settingsResponse.ok) {
          setProfileFeedback({
            type: "error",
            message:
              `Erro ao atualizar moeda padrão (status ${settingsResponse.status}).`,
          });
          return;
        }
      }

      setProfileFeedback({
        type: "success",
        message: "Perfil atualizado com sucesso!",
      });
      setIsEditingProfile(false);

      // Recarrega a sessão para obter os dados atualizados
      authClient.$store.notify("$sessionSignal");
      await sessionQuery.refetch();
    } catch (error) {
      console.error("Erro ao atualizar perfil:", error);
      setProfileFeedback({
        type: "error",
        message: "Erro ao atualizar perfil. Tente novamente.",
      });
    }
  };

  const handleRefreshSession = async () => {
    setIsRefreshing(true);
    try {
      // Desabilita o cache interno do BetterAuth e força nova requisição
      const sessionResponse = await authClient.getSession({
        query: {
          disableCookieCache: true,
        },
        fetchOptions: {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache, no-store, must-revalidate",
            "Pragma": "no-cache",
            "Expires": "0",
          },
        },
      });

      if (sessionResponse.data) {
        // Notifica mudança para forçar atualização da UI
        authClient.$store.notify("$sessionSignal");

        // Também faz refetch do query para garantir sincronização
        await sessionQuery.refetch();
      }
    } catch (error) {
      console.error("Erro ao atualizar sessão:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSignOut = async () => {
    try {
      // Limpa o usuário do Faro antes de fazer logout
      clearFaroUser();
      // Remove o token bearer do localStorage
      clearBearerToken();
      await authClient.signOut();
    } finally {
      navigate("/login", { replace: true });
    }
  };

  const handleCopyJwtToken = async () => {
    if (!jwtToken) return;

    try {
      await navigator.clipboard.writeText(jwtToken);
      setCopyJwtSuccess(true);
      setTimeout(() => setCopyJwtSuccess(false), 2000);
    } catch (error) {
      console.error("Erro ao copiar token MCP:", error);
    }
  };

  return (
    <div className="page-centered">
      <div className="profile-card">
        <h1>Perfil</h1>
        <div className="profile-body">
          {user.image
            ? (
              <img
                src={user.image}
                alt={user.name ?? user.email}
                className="profile-avatar"
              />
            )
            : (
              <div className="profile-avatar profile-avatar--placeholder">
                {(user.name ?? user.email).charAt(0).toUpperCase()}
              </div>
            )}

          <div className="profile-details">
            <dl>
              <div>
                <dt>Nome</dt>
                <dd>{user.name ?? "Não informado"}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  {isEditingEmail
                    ? (
                      <div className="email-edit-form">
                        <input
                          type="email"
                          value={newEmail}
                          onChange={(e) => setNewEmail(e.target.value)}
                          placeholder="novo@email.com"
                          className="email-input"
                          autoFocus
                        />
                        <div className="email-edit-actions">
                          <button
                            type="button"
                            className="email-save-btn"
                            onClick={handleUpdateEmail}
                          >
                            Salvar
                          </button>
                          <button
                            type="button"
                            className="email-cancel-btn"
                            onClick={handleCancelEditEmail}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )
                    : (
                      <div className="email-display">
                        <span>{user.email}</span>
                        <button
                          type="button"
                          className="email-edit-trigger"
                          onClick={handleStartEditEmail}
                          title="Editar email"
                        >
                          ✏️
                        </button>
                      </div>
                    )}
                </dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>
                  {user.emailVerified ? "Email verificado" : "Email pendente"}
                </dd>
              </div>
              <div>
                <dt>País</dt>
                <dd>
                  {isEditingProfile
                    ? (
                      <div className="email-edit-form">
                        <input
                          type="text"
                          value={country}
                          onChange={(e) => setCountry(e.target.value)}
                          placeholder="Brasil"
                          className="email-input"
                        />
                      </div>
                    )
                    : (
                      <div className="email-display">
                        <span>
                          {(user.country as string) ?? "Não informado"}
                        </span>
                        {!isEditingProfile && (
                          <button
                            type="button"
                            className="email-edit-trigger"
                            onClick={handleStartEditProfile}
                            title="Editar perfil"
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    )}
                </dd>
              </div>
              <div>
                <dt>Idioma</dt>
                <dd>
                  {isEditingProfile
                    ? (
                      <div className="email-edit-form">
                        <input
                          type="text"
                          value={language}
                          onChange={(e) => setLanguage(e.target.value)}
                          placeholder="Português"
                          className="email-input"
                        />
                      </div>
                    )
                    : (
                      <span>
                        {(userSettings?.language as string) ?? "Não informado"}
                      </span>
                    )}
                </dd>
              </div>
              <div>
                <dt>Moeda padrão</dt>
                <dd>
                  {isEditingProfile
                    ? (
                      <div className="email-edit-form">
                        <select
                          value={defaultCurrency}
                          onChange={(e) => setDefaultCurrency(e.target.value)}
                          className="email-input"
                        >
                          <option value="">Selecione...</option>
                          <option value="USD">USD</option>
                          <option value="BRL">BRL</option>
                          <option value="PYG">PYG</option>
                        </select>
                        <div className="email-edit-actions">
                          <button
                            type="button"
                            className="email-save-btn"
                            onClick={handleUpdateProfile}
                          >
                            Salvar
                          </button>
                          <button
                            type="button"
                            className="email-cancel-btn"
                            onClick={handleCancelEditProfile}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    )
                    : (
                      <span>
                        {(userSettings?.defaultCurrency as string) ??
                          "Não informado"}
                      </span>
                    )}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {emailFeedback && (
          <p
            className={emailFeedback.type === "success"
              ? "auth-success"
              : "auth-error"}
          >
            {emailFeedback.message}
          </p>
        )}

        {profileFeedback && (
          <p
            className={profileFeedback.type === "success"
              ? "auth-success"
              : "auth-error"}
          >
            {profileFeedback.message}
          </p>
        )}

        {!user.emailVerified && user.email && (
          <div className="email-verification-warning">
            <p>
              Seu email ainda não foi verificado. Verifique sua caixa de entrada
              ou clique no botão abaixo para receber um novo código.
            </p>
            <button
              type="button"
              className="link-button verify-email-link"
              onClick={() =>
                navigate("/verify-email", {
                  state: { email: user.email },
                })}
            >
              Verificar email agora →
            </button>
          </div>
        )}

        {passkeyFeedback && (
          <p
            className={passkeyFeedback.type === "success"
              ? "auth-success"
              : "auth-error"}
          >
            {passkeyFeedback.message}
          </p>
        )}

        {/* Resumo da Wallet (/wallet/summary) */}
        <div
          style={{
            marginTop: "1rem",
            marginBottom: "1rem",
            padding: "1rem",
            background: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              marginBottom: "0.75rem",
              fontSize: "0.9rem",
              color: "#666",
            }}
          >
            Resumo da Wallet (/wallet/summary):
          </h3>
          {walletSummaryLoading && (
            <p style={{ fontSize: "0.85rem", color: "#666" }}>Carregando...</p>
          )}
          {walletSummaryError && (
            <p style={{ fontSize: "0.85rem", color: "#e53e3e" }}>
              Erro: {walletSummaryError}
            </p>
          )}
          {walletSummary && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                gap: "0.75rem",
              }}
            >
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  Equity total
                </div>
                <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                  {toNumber(walletSummary.totalEquity).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  Saldo da carteira
                </div>
                <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                  {toNumber(walletSummary.totalWalletBalance).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  Disponível
                </div>
                <div style={{ fontSize: "1.1rem", fontWeight: 600 }}>
                  {toNumber(walletSummary.totalAvailableBalance).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  PNL (USD)
                </div>
                <div
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: toNumber(walletSummary.pnlUSD) >= 0 ? "#10b981" : "#e53e3e",
                  }}
                >
                  {toNumber(walletSummary.pnlUSD).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  PNL total
                </div>
                <div
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: toNumber(walletSummary.pnlPercent) >= 0
                      ? "#10b981"
                      : "#e53e3e",
                  }}
                >
                  {toNumber(walletSummary.pnlPercent).toFixed(2)}%
                </div>
              </div>
              <div
                style={{
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "6px",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#888" }}>
                  Variação 24h
                </div>
                <div
                  style={{
                    fontSize: "1.1rem",
                    fontWeight: 600,
                    color: toNumber(walletSummary.pnl24hPercent) >= 0
                      ? "#10b981"
                      : "#e53e3e",
                  }}
                >
                  {toNumber(walletSummary.pnl24hPercent).toFixed(2)}%
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="profile-actions">
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/referral")}
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #a855f7 100%)",
            }}
          >
            🎁 Indique e ganhe
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/dcai")}
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
            }}
          >
            🤖 DCAI
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/crypto-ramp")}
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
            }}
          >
            💱 Crypto Ramp
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/market")}
            style={{
              background: "linear-gradient(135deg, #f7931a 0%, #ffad33 100%)",
            }}
          >
            📈 Market
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/portfolio/ativos")}
            style={{
              background: "linear-gradient(135deg, #3b5fd9 0%, #6366f1 100%)",
            }}
          >
            💼 Carteira
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/wallet/history")}
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
            }}
          >
            📜 Histórico
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/orders")}
            style={{
              background: "linear-gradient(135deg, #0ea5e9 0%, #3b5fd9 100%)",
            }}
          >
            📋 Minhas ordens
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/futures")}
            style={{
              background: "linear-gradient(135deg, #8b5cf6 0%, #d946ef 100%)",
            }}
          >
            📊 Futuros
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/wallet/withdraw/USDT")}
            style={{
              background: "linear-gradient(135deg, #3b5fd9 0%, #6366f1 100%)",
            }}
          >
            🏧 Sacar (USDT)
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/chat")}
            style={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            }}
          >
            💬 Abrir Chat IA
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/checklist")}
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            }}
          >
            ✅ Ver Checklist
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={() => navigate("/kyc/id-and-liveness")}
            style={{
              background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
            }}
          >
            🔐 Verificação KYC
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={handleConfigurePasskey}
            disabled={isConfiguringPasskey}
          >
            {isConfiguringPasskey
              ? "Configurando passkey..."
              : "Configurar passkey"}
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={handleRefreshSession}
            disabled={isRefreshing}
          >
            {isRefreshing ? "Atualizando..." : "Atualizar sessão"}
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={handleSignOut}
          >
            Sair
          </button>
        </div>

        {/* Widget de MCP Delegate token (GET /mcp/my-token) */}
        <div
          style={{
            marginTop: "2rem",
            padding: "1rem",
            background: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              marginBottom: "0.5rem",
              fontSize: "0.9rem",
              color: "#666",
            }}
          >
            MCP Delegate token
          </h3>
          {jwtLoading && (
            <p style={{ fontSize: "0.85rem", color: "#666" }}>Carregando...</p>
          )}
          {jwtError && (
            <p style={{ fontSize: "0.85rem", color: "#e53e3e" }}>
              Erro: {jwtError}
            </p>
          )}
          {jwtToken && (
            <div>
              <div
                style={{
                  fontSize: "0.75rem",
                  background: "#fff",
                  padding: "0.75rem",
                  borderRadius: "4px",
                  overflow: "auto",
                  marginBottom: "0.75rem",
                  fontFamily: "monospace",
                  wordBreak: "break-all",
                  maxHeight: "150px",
                }}
              >
                {jwtToken}
              </div>
              <button
                type="button"
                onClick={handleCopyJwtToken}
                style={{
                  padding: "0.5rem 1rem",
                  fontSize: "0.85rem",
                  background: copyJwtSuccess ? "#48bb78" : "#4299e1",
                  color: "#fff",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: "500",
                  transition: "background 0.2s",
                }}
              >
                {copyJwtSuccess ? "Copiado!" : "Copiar token"}
              </button>
            </div>
          )}
        </div>

        {/* Resultado da API de exemplo */}
        <div
          style={{
            marginTop: "1.5rem",
            padding: "1rem",
            background: "#f5f5f5",
            borderRadius: "8px",
          }}
        >
          <h3
            style={{
              marginBottom: "0.5rem",
              fontSize: "0.9rem",
              color: "#666",
            }}
          >
            API Response ({apiEndpoint}):
          </h3>
          {apiLoading && (
            <p style={{ fontSize: "0.85rem", color: "#666" }}>Carregando...</p>
          )}
          {apiError && (
            <p style={{ fontSize: "0.85rem", color: "#e53e3e" }}>
              Erro: {apiError}
            </p>
          )}
          {apiResponse !== null && (
            <pre
              style={{
                fontSize: "0.75rem",
                background: "#fff",
                padding: "0.75rem",
                borderRadius: "4px",
                overflow: "auto",
                maxHeight: "200px",
                margin: 0,
              }}
            >
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
}
